import { PullIndicator } from '@nexus/ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findAppUpdate } from '../offline/app-update';
import { useSyncStatus } from '../sync/sync-status';
import { PULL_THRESHOLD, usePullToRefresh } from './use-pull-to-refresh';

/**
 * Pull to refresh for the app area: loads every query of the open page again (session included),
 * syncs offline data and looks for a new version of the app. Only a new version loads the whole
 * page; otherwise inputs and scroll position stay. Offline it only springs back: the sync status
 * in the top bar already says why (docs/architecture/offline.md).
 */
export function PullToRefresh() {
  const { t } = useTranslation();
  const { syncNow } = useSyncStatus();
  const [announcement, setAnnouncement] = useState('');

  const refresh = useCallback(async () => {
    setAnnouncement('');
    if (!navigator.onLine) return;
    const [, update] = await Promise.all([syncNow(), findAppUpdate()]);
    if (update) {
      window.location.reload();
      return;
    }
    setAnnouncement(t('shell.refreshed'));
  }, [syncNow, t]);

  const { distance, refreshing } = usePullToRefresh(refresh);

  return (
    <>
      <PullIndicator distance={distance} threshold={PULL_THRESHOLD} refreshing={refreshing} />
      <span className="sr-only" role="status">
        {announcement}
      </span>
    </>
  );
}
